/**
 * Water target presets — "what should my water look like for this beer?"
 * (PLAN §4.3: *"Target: pick a preset profile … or style-suggested targets"*).
 *
 * These are **editorial recommendations, not analyses.** A city profile is a
 * measurement someone took; a target profile is a judgement about where the
 * flavour lands. Every entry here is therefore `representative` — the tier that
 * means "good enough to design a recipe with, never quoted as fact" — and every
 * one carries a one-line `rationale` so the UI can say *why* rather than just
 * dropping six numbers into a form.
 *
 * ## Bicarbonate is a target too
 *
 * The pale targets ask for **zero** bicarbonate, which is not squeamishness: a
 * pale grist has no roasted malt acidity to spend alkalinity against, so any
 * alkalinity that survives into the mash pushes the pH up and has to be titrated
 * back out with acid. The dark target asks for a lot of it for the mirror-image
 * reason. This is the one field where "aim for what the tap gives you" is
 * actively wrong advice, so it is stated explicitly on every preset.
 *
 * ## The SO₄:Cl band
 *
 * `sulfateChlorideBand` is the range the preset is *aiming* at, and it is
 * always wider than the ratio the exact ion numbers produce — the ratio is a
 * flavour heuristic, and pretending it is precise to two decimal places would
 * be a lie. The band cut points line up with core's descriptive bands
 * (< 0.5 too malty · 0.5–1 malty · 1–2 balanced · 2–4 hoppy · ≥ 4 very hoppy,
 * after Palmer & Kaminski, *Water*, 2013 ch. 4).
 */

import { z } from 'zod';
import { descriptionSchema, provenanceSchema, range, slugSchema } from './common';

/** ppm (mg/L) of one ion in a target profile. */
const targetIonPpmSchema = z.number().min(0).max(1000);

/** A recommended brewing water target. */
export const waterTargetPresetSchema = z.strictObject({
  /** Stable slug, e.g. `hoppy-pale`. */
  id: slugSchema,
  /** Display name, e.g. `Hoppy pale`. */
  name: z.string().min(3).max(60),
  /** Two or three sentences in our own words. */
  description: descriptionSchema,
  /** One line the UI shows next to the preset in the picker. */
  rationale: z.string().min(20).max(200),
  /** Ca²⁺, ppm. */
  calciumPpm: targetIonPpmSchema,
  /** Mg²⁺, ppm. */
  magnesiumPpm: targetIonPpmSchema,
  /** Na⁺, ppm. */
  sodiumPpm: targetIonPpmSchema,
  /** Cl⁻, ppm. */
  chloridePpm: targetIonPpmSchema,
  /** SO₄²⁻, ppm. */
  sulfatePpm: targetIonPpmSchema,
  /** HCO₃⁻, ppm. Alkalinity as CaCO₃ = this × 0.820162. */
  bicarbonatePpm: targetIonPpmSchema,
  /** The SO₄:Cl range this preset aims at. Always wider than the ions' exact ratio. */
  sulfateChlorideBand: range(0, 20),
  /** Search/filter tags: `pale`, `hoppy`, `dark`, `lager`. */
  tags: z.array(z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)).max(12),
  /** @see provenanceSchema — always `representative`; see the module note. */
  provenance: provenanceSchema,
});

/** @see waterTargetPresetSchema */
export type WaterTargetPreset = z.infer<typeof waterTargetPresetSchema>;
